import React from 'react';

const RELOAD_KEY = 'chunk-reload';
const RELOAD_KEY_CLEAR_DELAY_MS = 10_000;

// Reset the flag only after the app has run healthy for a while post-reload.
// Clearing it immediately at module scope would defeat the loop guard: chunk
// failures always happen after module evaluation, so reloadOnce() would never
// see the flag and a persistent failure (ad-blocker, proxy, stale CDN HTML)
// would reload the page forever.
if (sessionStorage.getItem(RELOAD_KEY)) {
	setTimeout(() => {
		sessionStorage.removeItem(RELOAD_KEY);
	}, RELOAD_KEY_CLEAR_DELAY_MS);
}

// Covers the different per-browser wordings for a failed dynamic import,
// which happen when an old asset manifest points at chunks that no longer
// exist after a deployment.
const CHUNK_ERROR_PATTERNS = [
	'Failed to fetch dynamically imported module',
	'Importing a module script failed',
	'error loading dynamically imported module',
	'dynamically imported module',
	'Unable to preload CSS',
	'ChunkLoadError',
];

export function isChunkLoadError(error) {
	const message = error?.message || '';
	return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

/**
 * Reloads the page a single time to pick up the new asset manifest, using
 * sessionStorage to prevent reload loops. Returns true if a reload was
 * triggered, false if we've already reloaded recently.
 *
 * Deliberately does NOT consume the flag when returning false: a single chunk
 * failure reaches this twice (the lazy import's catch and the ErrorBoundary's
 * logError), and consuming the flag on the first call would let the second
 * call trigger another reload — recreating the reload loop. The delayed
 * module-scope timer above owns clearing the flag.
 */
export function reloadOnce() {
	if (sessionStorage.getItem(RELOAD_KEY)) {
		return false;
	}

	sessionStorage.setItem(RELOAD_KEY, '1');
	window.location.reload();
	return true;
}

/**
 * Wraps React.lazy with retry logic that handles stale chunk failures
 * after deployments. On import failure, reloads the page once to pick
 * up the new asset manifest. Uses sessionStorage to prevent reload loops.
 */
export function lazyWithRetry(importFn) {
	return React.lazy(() =>
		importFn().catch((error) => {
			if (!isChunkLoadError(error)) throw error;

			if (reloadOnce()) {
				return new Promise(() => {});
			}

			throw error;
		})
	);
}
